import * as RadPopover from '@radix-ui/react-popover'
import { useAction } from '@reatom/npm-react'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { Deeplink, DeeplinkURL } from '@root/entities/deeplinking'
import { UserShort } from '@root/entities/user-short'
import { useClipboard } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { KebabHorisontalExactIcon, Separator } from '@root/shared/ui-kit'

import { startReportUserFlowAction } from '../report'

import type { ButtonProps } from '@blade/ui-kit'
import type { DelugeId } from '@root/entities/user-short'

interface ActivityKebabActionsProps {
  userShort: UserShort.UserShort
  size?: ButtonProps['size']
  onNavigateToUserProfile: (delugeId: DelugeId) => void
}

export const ActivityKebabActions = ({
  userShort,
  size = 'xs',
  onNavigateToUserProfile,
}: ActivityKebabActionsProps) => {
  const { t } = useTranslation()
  const { copyToClipboard } = useClipboard()
  const startReportUserFlow = useAction(startReportUserFlowAction)

  const url = DeeplinkURL.fromDeeplink(
    new Deeplink.UserProfile({ delugeId: userShort.delugeId }),
  )

  return (
    <Root modal={true}>
      <RadPopover.Trigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={size}
          radius="rounded"
          iconLeft={<KebabHorisontalExactIcon size={20} />}
        />
      </RadPopover.Trigger>
      <RadPopover.Portal>
        <Content side="bottom" sideOffset={4} align="end">
          <Item
            onClick={() => {
              startReportUserFlow({ user: userShort })
            }}
          >
            {t('common.report')}
          </Item>
          <ItemSeparator />
          <Item onClick={() => onNavigateToUserProfile(userShort.delugeId)}>
            {t('common.profile')}
          </Item>
          <ItemSeparator />
          <Item
            onClick={() =>
              navigator.share({
                title: t('common.profile'),
                url: url.toString(),
                text: UserShort.someName(userShort),
              })
            }
          >
            {t('common.share')}
          </Item>
          <ItemSeparator />
          <Item onClick={() => copyToClipboard(url.toString())}>
            {t('common.copy_address')}
          </Item>
          <ItemSeparator />
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

export const Root = styled(RadPopover.Root)<{}>`
  display: flex;
  gap: 10px;
`

export const Content = styled(RadPopover.Content)`
  background: var(--background-1);
  color: var(--text-10);

  overflow: hidden;
  border-radius: 10px;
  z-index: 11;

  display: flex;
  flex-direction: column;
  padding: 3px;
  gap: 2px;
`

const Item = styled(RadPopover.Close)`
  all: unset;
  cursor: pointer;
  padding: 6px;
  transition: all 100ms ease-in;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  border-radius: 8px;

  &:hover {
    color: var(--appearance-inv-1);
    background: var(--backgroundA-2);
  }
`

export const ItemSeparator = styled(Separator)`
  margin: 0 10px;
  background: var(--backgroundA-2);
`
